import { isStreamDebug, streamLog } from "./debug";
import {
  normalizeHistoryMessage,
  normalizeInterpretation,
  normalizeStable,
  normalizeTypeToTalk
} from "./normalize";
import { finalizeAll, hydrateHistory, reduce } from "./reducer";
import { NormalizedEvent, Party, StreamState, createInitialState } from "./types";

type Listener = () => void;
type PendingItem = { kind: "stable" | "interpretation"; data: Record<string, any> };

const historyTextKey = (party: Party, text: string): string =>
  `${party}:${text.trim().toLowerCase().replace(/\s+/g, " ")}`;

export class TranscriptStore {
  private state: StreamState;
  private listeners = new Set<Listener>();
  private callId?: string;
  private hydrated = false;
  private holding = false;
  private pending: PendingItem[] = [];

  constructor(callId?: string) {
    this.callId = callId;
    this.state = createInitialState(callId);
  }

  holdUntilHistory(): void {
    if (!this.hydrated) this.holding = true;
  }

  getState(): StreamState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private set(next: StreamState): void {
    if (next === this.state) return;
    this.state = next;
    this.listeners.forEach((l) => l());
  }

  private dispatch(event: NormalizedEvent | null): void {
    if (!event) return;
    if (this.callId && event.callId && event.callId !== this.callId) {
      streamLog("store", "drop cross-call event", {
        expected: this.callId,
        got: event.callId,
        kind: event.kind,
        party: event.party
      });
      return;
    }
    this.set(reduce(this.state, event));
  }

  private rawTrace(kind: "stable" | "interpretation", data: unknown): void {
    if (!isStreamDebug()) return;
    try {
      // eslint-disable-next-line no-console
      console.log(`CONVEY_RAW ${kind} ${JSON.stringify(data)}`);
    } catch {
      /* circular / unserializable payload — skip */
    }
  }

  ingestStable(data: Record<string, any>): void {
    this.rawTrace("stable", data);
    this.enqueueOrDispatch("stable", data);
  }

  ingestInterpretation(data: Record<string, any>): void {
    this.rawTrace("interpretation", data);
    this.enqueueOrDispatch("interpretation", data);
  }

  ingestTypeToTalk(englishText: string, foreignText?: string): void {
    this.dispatch(normalizeTypeToTalk(englishText, foreignText));
  }

  loadHistory(rows: Array<Record<string, any>>): void {
    if (this.hydrated) {
      streamLog("store", "loadHistory ignored (already hydrated)", {
        callId: this.callId,
        rows: rows.length
      });
      return;
    }
    const events = rows
      .map((r) => normalizeHistoryMessage(r))
      .filter((e): e is NormalizedEvent => !!e);
    this.state = hydrateHistory(events, this.callId);
    this.hydrated = true;
    this.holding = false;
    streamLog("store", "loadHistory hydrated", {
      callId: this.callId,
      rows: rows.length,
      cards: this.state.cardOrder.length,
      pending: this.pending.length
    });
    this.flushPending();
    this.listeners.forEach((l) => l());
  }

  private enqueueOrDispatch(
    kind: "stable" | "interpretation",
    data: Record<string, any>
  ): void {
    if (this.holding) {
      this.pending.push({ kind, data });
      return;
    }
    this.dispatch(kind === "stable" ? normalizeStable(data) : normalizeInterpretation(data));
  }

  private collectHistoryTexts(): Set<string> {
    const keys = new Set<string>();
    for (const id of Object.keys(this.state.segments)) {
      const seg = this.state.segments[id];
      if (!seg.historical) continue;
      const text = (seg.interp || seg.rolling || "").trim();
      if (text) keys.add(historyTextKey(seg.party, text));
    }
    return keys;
  }

  private isHistoryDuplicate(event: NormalizedEvent, historyKeys: Set<string>): boolean {
    if (event.kind !== "transcript") return false;
    const text = (event.english || event.original || "").trim();
    if (!text) return false;
    return historyKeys.has(historyTextKey(event.party, text));
  }

  private sortPending(items: PendingItem[]): PendingItem[] {
    return items.slice().sort((a, b) => {
      const ao = typeof a.data.orderSeq === "number" ? a.data.orderSeq : Number.MAX_SAFE_INTEGER;
      const bo = typeof b.data.orderSeq === "number" ? b.data.orderSeq : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      const as = typeof a.data.seq === "number" ? a.data.seq : -1;
      const bs = typeof b.data.seq === "number" ? b.data.seq : -1;
      if (as !== bs) return as - bs;
      const ad = typeof a.data.displaySeq === "number" ? a.data.displaySeq : 0;
      const bd = typeof b.data.displaySeq === "number" ? b.data.displaySeq : 0;
      if (ad !== bd) return ad - bd;
      const at = Date.parse(a.data.createdAt) || 0;
      const bt = Date.parse(b.data.createdAt) || 0;
      return at - bt;
    });
  }

  private flushPending(): void {
    if (this.pending.length === 0) return;
    const queued = this.sortPending(this.pending);
    this.pending = [];
    const historyKeys = this.collectHistoryTexts();
    const skippedSegmentIds = new Set<string>();
    streamLog("store", "flushPending", { events: queued.length });
    for (const item of queued) {
      if (item.kind === "stable") {
        const event = normalizeStable(item.data);
        if (!event) continue;
        if (this.isHistoryDuplicate(event, historyKeys)) {
          if (event.segmentId) skippedSegmentIds.add(event.segmentId);
          streamLog("store", "flushPending skip history duplicate", {
            party: event.party,
            segmentId: event.segmentId
          });
          continue;
        }
        this.dispatch(event);
      } else {
        const event = normalizeInterpretation(item.data);
        if (!event) continue;
        if (event.segmentId && skippedSegmentIds.has(event.segmentId)) continue;
        this.dispatch(event);
      }
    }
  }

  finalizeAll(): void {
    streamLog("store", "finalizeAll", {
      callId: this.callId,
      cards: this.state.cardOrder.length
    });
    this.set(finalizeAll(this.state));
  }
}
